<script setup lang="ts">
    import {
        computed,
        inject,
        toRef,
        useTemplateRef,
        watch,
        watchEffect,
        useSlots,
    } from 'vue'
    import { useElementSize } from '@vueuse/core'
    import { storeToRefs } from 'pinia'
    import { useI18n } from 'vue-i18n'
    import type {
        AgentInterface,
        AgentModel,
        AgentFileUpload,
        AgentGatewayOptions,
        AgentVoiceCapability,
        ChatMessageActions,
        RevisedAnswer,
        UIChatMessage,
        AgentTools,
        HostToolsCapability,
        PageAccessCapability,
    } from 'models'
    import {
        type RegisteredHostTool,
        useChatbotStore,
        useLocalizedString,
        provideShortUrlResolver,
    } from 'composables'
    import PkAvatar from './PkAvatar.vue'
    import PkChatbotViewChat from './PkChatbotViewChat.vue'
    import PkChatbotViewConversations from './PkChatbotViewConversations.vue'
    import PkChatbotViewFiles from './PkChatbotViewFiles.vue'
    import PkChatbotViewProfile from './PkChatbotViewProfile.vue'
    import PkStreamingMarkdown from './PkStreamingMarkdown.vue'
    import { ChatbotProfileContextKey } from './chatbotProfileContext'
    import { provideChatImagePolicy } from './chatImagePolicy'
    import { agentThemeStyle, resolveContrastColor } from './utils'

    // Captured in script setup to avoid TS7022 circular inference in template
    const slots = useSlots()

    const { t: $t } = useI18n({
        useScope: 'global',
    })

    const props = defineProps<{
        baseUrl: string
        basePath?: string
        agentId: string
        agentType?: 'reasoning' | 'chat'
        userId?: string
        name?: string
        model?: string
        agentModel?: AgentModel
        agentTools?: AgentTools
        agentInterface?: AgentInterface
        actions?: ChatMessageActions[]
        revisedAnswers?: RevisedAnswer[]
        headers?: Record<string, string>
        systemTheme?: 'light' | 'dark'
        agentFileUpload?: AgentFileUpload
        /** Voice configuration from the agent settings: enables the talk button. */
        voice?: AgentVoiceCapability
        fallbackModels?: string[]
        gatewayOptions?: Partial<AgentGatewayOptions>
        externalContext?: Record<string, unknown>
        /** Host tools as the agent allows them (`capabilities.hostTools` of the public agent). */
        hostTools?: HostToolsCapability
        /** Tools the embedding app registered, with the function that runs each (spec `host-tools.md`). */
        hostToolRegistry?: RegisteredHostTool[]
        /** Page access as the agent allows it (`capabilities.pageAccess`, spec `page-tools.md`). */
        pageAccess?: PageAccessCapability
        /** The widget's page tools: only the web component provides them. */
        pageToolRegistry?: RegisteredHostTool[]
        /** The tools of the forms annotated on the page now, read on every request. */
        pageForms?: () => RegisteredHostTool[]
        /** Layout modifier: `widget` (default) keeps the contained layout with header, `fullscreen` removes header/background for full-page usage (the page provides the chrome), `panel` keeps the header but slims it down for a docked side panel (the host provides background and border) */
        modifier?: 'widget' | 'fullscreen' | 'panel'
        /** Display name of the current user, used for the `{name}` placeholder in the fullscreen greeting */
        userName?: string
        /** Show the "Files in this chat" menu for hosts that don't provide the
         * profile context but whose user is authenticated (e.g. the backoffice
         * playground). The widget host relies on `canManageProfile` instead. */
        userFilesEnabled?: boolean
        /** The host can close the chat: the header menu offers "Close", which emits `close`. */
        closable?: boolean
    }>()
    const emit = defineEmits<{
        'message-update': [message: UIChatMessage]
        'show-info': [message: UIChatMessage]
        revise: [message: UIChatMessage]
        error: [error: { type: string; title: string; status: number }]
        /** That conversation is loaded: a message sent now lands in it. */
        loaded: [chatId: string]
        /** The user chose "Close" in the header menu. */
        close: []
    }>()

    const chatId = defineModel<string | undefined>('chatId', {
        type: String,
        default: undefined,
    })

    const store = useChatbotStore(props.agentId)

    // Resolve backend short-urls (`/api/r/<hash>`) back to their real target
    // for display in tool components. Batched + cached across the chat tree.
    provideShortUrlResolver((hashes) =>
        store.apiClient.resolveShortUrls(hashes),
    )

    const {
        messages,
        localChatId,
        activeView,
        chatError,
        isDark,
        pageAccessAvailable,
        pageAccessOff,
    } = storeToRefs(store)

    // Ref to store.agentId for watchers (avoids duplicate-key with agentId prop)
    const storeAgentId = toRef(store, 'agentId')

    const {
        startNewChat,
        stopGeneration,
        regenerate,
        navigate,
        sendMessage,
        setPageAccessOff,
    } = store

    // Images in the answers and the tool cards load by themselves only from
    // the backend this chat talks to and from the hosts the agent lists.
    provideChatImagePolicy(
        () => props.agentInterface?.imageHosts,
        () => props.baseUrl,
    )

    // Optional profile-management context provided by the host widget.
    // Present only for authenticated, non-anonymous sessions.
    const profile = inject(ChatbotProfileContextKey, undefined)
    const canManageProfile = computed(
        () => profile?.canManageProfile.value ?? false,
    )
    // Anonymous users get a "Sign in" entry instead of the profile one,
    // provided the host filled the `view-auth` slot and the agent has at
    // least one auth method enabled.
    const canSignIn = computed(
        () =>
            !canManageProfile.value &&
            (profile?.canSignIn?.value ?? false) &&
            slots['view-auth'] !== undefined,
    )

    // Once the sign-in completes (the session becomes non-anonymous) leave
    // the auth view and return to the conversation.
    watch(canManageProfile, (canManage) => {
        if (canManage && activeView.value === 'auth') {
            navigate('chat')
        }
    })

    // Sync props → store reactively
    watchEffect(() => {
        store.baseUrl = props.baseUrl
        store.basePath = props.basePath
        store.name = props.name
        store.agentType = props.agentType
        store.userId = props.userId
        store.agentInterface = props.agentInterface
        store.agentModel = props.agentModel
        store.agentTools = props.agentTools
        store.headers = props.headers
        store.revisedAnswers = props.revisedAnswers
        store.model = props.model
        store.actionsInput = props.actions
        store.agentFileUpload = props.agentFileUpload
        store.agentVoice = props.voice
        store.fallbackModels = props.fallbackModels
        store.gatewayOptions = props.gatewayOptions
        store.externalContext = props.externalContext
        store.agentHostTools = props.hostTools
        store.hostToolRegistry = props.hostToolRegistry
        store.agentPageAccess = props.pageAccess
        store.pageToolRegistry = props.pageToolRegistry
        store.pageFormsProvider = props.pageForms
        store.systemTheme = props.systemTheme
        store.hideWelcomeMessage = props.modifier === 'fullscreen'
    })

    // Sync chatId v-model ↔ store (bidirectional)
    // Parent → store: if the parent provides an external chatId, push it into
    // the store so the Chat instance picks it up.
    watch(
        () => chatId.value,
        (id) => {
            if (id && id !== store.localChatId) {
                store.localChatId = id
            }
        },
        { immediate: true },
    )
    // Store → parent: keep the v-model in sync whenever the store changes
    // the chatId internally (e.g. startNewChat).
    watch(localChatId, (id) => {
        chatId.value = id
    })

    // Emit structured errors from chat to the parent component
    watch(chatError, (error) => {
        if (!error) {
            return
        }
        try {
            const parsedError = JSON.parse(error.message) as {
                type: string
                title: string
                status: number
            }
            emit('error', parsedError)
        } catch {
            // ignore non-JSON errors
        }
    })

    // ui
    const isFullscreen = computed(() => props.modifier === 'fullscreen')
    const isPanel = computed(() => props.modifier === 'panel')

    /**
     * How much room the chat has, measured on the chat and not on the window.
     *
     * The same chat renders in a phone page, in a 408px widget, in a docked
     * panel and in a preview frame that the side panels squeeze to phone width
     * on a desktop screen, so the viewport says nothing about the space the
     * layout actually has. A CSS container query would read the same number
     * with no JavaScript, but `container-type` makes the element a containing
     * block for `position: fixed` descendants on every engine except Chrome
     * 129 and later, which displaces every dropdown in the chat: see
     * docs/agents/gotchas.md, "The chat measures its own width, and not with a
     * container query".
     *
     * The thresholds carry the modifiers, so the CSS stays declarative:
     * `--phone` has as much room as a phone page, `--tight` is too tight for
     * the agent chip beside the field, `--wide` has room for the outline rail
     * beside the reading column. Before the first measurement the width is 0
     * and no modifier applies, which is the roomy layout the base CSS writes.
     */
    const chatbotEl = useTemplateRef<HTMLDivElement>('chatbotEl')
    const { width: chatbotWidth } = useElementSize(chatbotEl)
    const isPhoneWidth = computed(
        () => chatbotWidth.value > 0 && chatbotWidth.value < 576,
    )
    const isTightWidth = computed(
        () => chatbotWidth.value > 0 && chatbotWidth.value < 640,
    )
    const isWideWidth = computed(() => chatbotWidth.value >= 848)
    const isReasoningAgent = computed(() => props.agentType === 'reasoning')
    // The notice says how a reasoning agent reads a message: it stands before
    // the first one, then leaves the room to the answers (pinned, it took a
    // third of a phone screen in the reasoning playground)
    const showReasoningNotice = computed(
        () =>
            isReasoningAgent.value &&
            !messages.value.some((message) => message.role === 'user'),
    )
    // A docked panel keeps a neutral header whatever the agent asks: it sits
    // beside the site or the app as part of the frame, and a colored band
    // made it read as a second site next to the first one.
    const useColorsForAgentHeader = computed(
        () => !isPanel.value && props.agentInterface?.useColorsForAgentHeader,
    )
    // Panel mode promotes the everyday actions (new chat, history) from the
    // dropdown to direct header buttons: the dropdown then only remains for
    // the secondary entries (files, profile, sign-in) and disappears when
    // none of them applies.
    const showHeaderMenu = computed(
        () =>
            !isPanel.value ||
            canManageProfile.value ||
            canSignIn.value ||
            pageAccessAvailable.value ||
            props.closable ||
            (!!props.userFilesEnabled && !!localChatId.value),
    )
    const themeClass = computed(() => [
        'theme',
        `theme--${isDark.value ? 'dark' : 'light'}`,
    ])
    const mainColor = computed(() => props.agentInterface?.mainColor)
    const contrastColor = computed(() =>
        resolveContrastColor(props.agentInterface?.textColor, mainColor.value),
    )
    const themeStyle = computed(() =>
        agentThemeStyle(mainColor.value, contrastColor.value),
    )
    const footerMessage = useLocalizedString(
        () => props.agentInterface?.footerMessage,
    )
    const title = computed(() => {
        if (activeView.value === 'conversations') {
            return $t('label.conversations')
        }
        if (activeView.value === 'profile') {
            return $t('label.profile')
        }
        if (activeView.value === 'auth') {
            return $t('label.authentication')
        }
        if (activeView.value === 'files') {
            return $t('userFiles.chatFilesTitle')
        }
        return props.name
    })

    // state
    const activeMessage = computed(() => {
        return messages.value?.[messages.value.length - 1]
    })

    // expose
    defineExpose({
        startNewChat,
        regenerate,
        stopGeneration,
        sendMessage,
    })

    // load chat, conversations and feedbacks on agent or chat change
    watch(
        [storeAgentId, localChatId],
        async ([newAgentId, newChatId]) => {
            await store.initialize(newAgentId, newChatId)
            emit('loaded', newChatId)
        },
        {
            immediate: true,
        },
    )
    // load conversations when navigating to conversations view
    watch(activeView, (newView) => {
        if (newView === 'conversations') {
            store.loadConversations(storeAgentId.value)
        } else if (newView === 'files') {
            store.loadChatFiles()
        }
    })
    // emit message updates for up-to-date context in parent components
    watch(
        activeMessage,
        (message) => {
            if (message) {
                emit('message-update', message)
            }
        },
        {
            deep: true,
            immediate: true,
        },
    )
</script>

<template>
    <div
        ref="chatbotEl"
        class="pk-chatbot"
        :class="[
            themeClass,
            {
                'pk-chatbot--fullscreen': isFullscreen,
                'pk-chatbot--panel': isPanel,
                'pk-chatbot--phone': isPhoneWidth,
                'pk-chatbot--tight': isTightWidth,
                'pk-chatbot--wide': isWideWidth,
            },
        ]"
        :style="themeStyle">
        <!-- #region title -->
        <div
            v-if="!isFullscreen"
            class="pk-chatbot__header"
            :style="
                useColorsForAgentHeader
                    ? {
                          backgroundColor: mainColor,
                      }
                    : undefined
            ">
            <div class="pk-chatbot__header-start">
                <template v-if="activeView !== 'chat'">
                    <VvButton
                        v-bind="{
                            modifiers: useColorsForAgentHeader
                                ? `action-quiet-${contrastColor}`
                                : 'action-quiet',
                            icon: 'ri:arrow-left-s-line',
                            title: $t('action.goBack'),
                        }"
                        @click="navigate('chat')" />
                    <VvIcon
                        :name="
                            activeView === 'conversations'
                                ? 'ri:history-line'
                                : activeView === 'files'
                                  ? 'ri:attachment-2'
                                  : 'ri:user-line'
                        "
                        class="pk-chatbot__nav-icon"
                        :style="
                            useColorsForAgentHeader
                                ? {
                                      color: contrastColor,
                                  }
                                : undefined
                        " />
                </template>
                <!-- Panel mode: no avatar, the docked panel identifies the
                     agent with the title alone -->
                <PkAvatar
                    v-else-if="!isPanel"
                    class="pk-chatbot__avatar"
                    :name
                    :img-src="agentInterface?.logo" />
                <strong
                    class="pk-chatbot__title"
                    :title="title"
                    :style="
                        useColorsForAgentHeader
                            ? {
                                  color: contrastColor,
                              }
                            : undefined
                    ">
                    {{ title }}
                </strong>
            </div>
            <VvButtonGroup class="pk-chatbot__header-end" modifiers="compact">
                <slot
                    name="header-actions"
                    v-bind="{
                        mainColor,
                        useColorsForAgentHeader,
                        contrastColor,
                        startNewChat,
                        stopGeneration,
                    }" />
                <template v-if="isPanel">
                    <VvButton
                        :modifiers="
                            useColorsForAgentHeader
                                ? `action-quiet-${contrastColor}`
                                : 'action-quiet'
                        "
                        icon="ri:edit-box-line"
                        :title="$t('action.startNewChat')"
                        @click="startNewChat()" />
                    <VvButton
                        v-if="!isReasoningAgent"
                        :modifiers="
                            useColorsForAgentHeader
                                ? `action-quiet-${contrastColor}`
                                : 'action-quiet'
                        "
                        icon="ri:history-line"
                        :title="$t('action.viewRecentChats')"
                        @click="navigate('conversations')" />
                </template>
                <VvDropdown
                    v-if="showHeaderMenu"
                    v-bind="{
                        placement: 'bottom-end',
                        modifiers: 'menu',
                        flip: true,
                        offset: 3,
                        strategy: 'fixed',
                    }">
                    <VvButton
                        v-bind="{
                            modifiers: useColorsForAgentHeader
                                ? `action-quiet-${contrastColor}`
                                : 'action-quiet',
                            icon: 'ri:more-2-fill',
                            title: $t('action.moreActions'),
                        }" />
                    <template #items>
                        <VvDropdownAction
                            v-if="!isPanel"
                            @click="startNewChat()">
                            <VvIcon name="ri:edit-box-line" />
                            {{ $t('action.startNewChat') }}
                        </VvDropdownAction>
                        <VvDropdownAction
                            v-if="!isReasoningAgent && !isPanel"
                            @click="navigate('conversations')">
                            <VvIcon name="ri:history-line" />
                            {{ $t('action.viewRecentChats') }}
                        </VvDropdownAction>
                        <VvDropdownAction
                            v-if="
                                (canManageProfile || userFilesEnabled) &&
                                localChatId
                            "
                            @click="navigate('files')">
                            <VvIcon name="ri:attachment-2" />
                            {{ $t('userFiles.chatFilesTitle') }}
                        </VvDropdownAction>
                        <!-- The user's own switch for the page tools, for
                             this tab (spec page-tools.md) -->
                        <VvDropdownAction
                            v-if="pageAccessAvailable"
                            @click="setPageAccessOff(!pageAccessOff)">
                            <VvIcon
                                :name="
                                    pageAccessOff
                                        ? 'ri:pages-line'
                                        : 'ri:forbid-line'
                                " />
                            {{
                                pageAccessOff
                                    ? $t('action.enablePageAccess')
                                    : $t('action.disablePageAccess')
                            }}
                        </VvDropdownAction>
                        <VvDropdownAction
                            v-if="canManageProfile"
                            @click="navigate('profile')">
                            <VvIcon name="ri:user-line" />
                            {{ $t('label.profile') }}
                        </VvDropdownAction>
                        <VvDropdownAction
                            v-else-if="canSignIn"
                            @click="navigate('auth')">
                            <VvIcon name="ri:account-circle-line" />
                            {{ $t('action.signIn') }}
                        </VvDropdownAction>
                        <VvDropdownAction
                            v-if="closable"
                            @click="emit('close')">
                            <VvIcon name="ri:close-line" />
                            {{ $t('action.close') }}
                        </VvDropdownAction>
                    </template>
                </VvDropdown>
                <slot
                    name="actions"
                    v-bind="{
                        mainColor,
                        useColorsForAgentHeader,
                        contrastColor,
                        startNewChat,
                        stopGeneration,
                    }" />
            </VvButtonGroup>
        </div>
        <!-- #endregion -->

        <div v-if="showReasoningNotice">
            <div class="pk-chatbot__warning-body">
                <VvAlert modifiers="callout">
                    <div class="pk-chatbot__warning-header">
                        <VvIcon
                            name="ri:alert-line"
                            class="pk-chatbot__warning-icon" />
                        <strong class="pk-chatbot__warning-title">
                            {{ $t('message.warning') }}
                        </strong>
                    </div>
                    <p class="pk-chatbot__warning-text">
                        {{ $t('message.reasoningAgent') }}
                    </p>
                </VvAlert>
            </div>
        </div>

        <!-- #region views -->
        <PkChatbotViewChat
            v-if="activeView === 'chat'"
            :agent-id
            :modifier
            :user-name
            @show-info="emit('show-info', $event)"
            @revise="emit('revise', $event)">
            <template
                v-for="slotName in Object.keys(slots)"
                #[slotName]="slotData"
                ><slot :name="slotName" v-bind="slotData"
            /></template>
        </PkChatbotViewChat>
        <PkChatbotViewConversations
            v-else-if="activeView === 'conversations'"
            :agent-id>
            <template
                v-for="slotName in Object.keys(slots)"
                #[slotName]="slotData"
                ><slot :name="slotName" v-bind="slotData"
            /></template>
        </PkChatbotViewConversations>
        <PkChatbotViewFiles v-else-if="activeView === 'files'" :agent-id />
        <div
            v-else-if="activeView === 'auth'"
            class="pk-chatbot__view-auth flex-1 min-h-0 overflow-y-auto">
            <!-- Sign-in form provided by the host widget (owns the auth client) -->
            <slot name="view-auth" />
        </div>
        <PkChatbotViewProfile v-else :agent-id>
            <template
                v-for="slotName in Object.keys(slots)"
                #[slotName]="slotData"
                ><slot :name="slotName" v-bind="slotData"
            /></template>
        </PkChatbotViewProfile>
        <!-- #endregion -->

        <!-- #region footer -->
        <div v-if="footerMessage" class="pk-chatbot__footer">
            <PkStreamingMarkdown :markdown="footerMessage" class="wysiwyg" />
        </div>
        <!-- #endregion -->
    </div>
</template>

<style lang="scss">
    .pk-chatbot {
        display: flex;
        flex-direction: column;
        position: relative;
        height: 100%;
        background-color: var(--color-surface);

        &__header {
            position: sticky;
            top: 0;
            z-index: 1;
            display: flex;
            justify-content: space-between;
            gap: var(--spacing-14);
            background-color: var(--color-surface-1);
            padding: var(--spacing-14);
            border-bottom: 1px solid var(--color-surface-3);
        }

        &__header-start {
            display: flex;
            align-items: center;
            gap: var(--spacing-sm);
            min-width: 0;
        }

        &__nav-icon {
            font-size: var(--text-18);
        }

        &__avatar {
            width: var(--spacing-24);
            height: var(--spacing-24);
        }

        &__title {
            font-size: var(--text-16);
            font-weight: var(--font-semibold);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &__header-end {
            flex-shrink: 0;
        }

        &__warning-body {
            padding: var(--spacing-md);
            border-bottom: 1px solid var(--color-surface-3);
        }

        &__warning-header {
            display: flex;
            align-items: center;
            gap: var(--spacing-4);
            margin-bottom: var(--spacing-4);
        }

        &__warning-icon {
            width: var(--spacing-16);
            height: var(--spacing-16);
        }

        &__warning-title {
            font-weight: 600;
            font-size: var(--text-14);
        }

        &__warning-text {
            font-size: var(--text-12);
            color: var(--color-word-3);
        }

        &__footer {
            padding: var(--spacing-md);
            border-top: 1px solid var(--color-surface-3);
            text-align: center;
            font-size: var(--text-12);
            color: var(--color-word-2);
        }

        // Modifier: fullscreen — layout full-page (Gemini-style): nessun
        // header interno, sfondo trasparente, input pill centrato.
        // Il chrome (sidebar, navigazione) è gestito dalla pagina ospite.
        &--fullscreen {
            // The dock, 60px. Declared rather than taken from the agent chip,
            // which the tight layout hides: the pill fell to 54px there and
            // the call bar that takes its place stayed at 60. There is no 60
            // in the spacing scale and a raw `60px` would stop following the
            // root font size, which the labels inside do follow.
            --pk-chat-fullscreen-dock-height: calc(
                var(--spacing-56) + var(--spacing-4)
            );

            // The pill, the notice that tucks into its top corners and the
            // call bar all round their ends on half of it.
            --pk-chat-dock-radius: calc(
                var(--pk-chat-fullscreen-dock-height) / 2
            );

            background-color: transparent;

            .pk-chatbot__footer {
                border-top: none;
                padding: 0 var(--spacing-md) var(--spacing-sm) var(--spacing-md);
            }

            // Reading column aligned with the input (Gemini-style). The
            // constraint lives on the inner wrapper so the scroll container
            // spans the page and the scrollbar sits at its edge.
            .pk-chatbot-messages {
                // Symmetric gutters: a visible scrollbar would otherwise
                // shrink the content box on one side only, shifting the
                // column off the input axis
                scrollbar-gutter: stable both-edges;

                // Dissolve the content at both edges (works on any page
                // background, dots included — a surface-colored fade band
                // would cut through the pattern instead)
                mask-image: linear-gradient(
                    to bottom,
                    transparent 0,
                    #000 var(--spacing-40),
                    #000 calc(100% - var(--spacing-40)),
                    transparent 100%
                );

                // Resting clearance so the first/last lines sit inside the
                // fully opaque zone
                padding-block: var(--spacing-48);
            }

            .pk-chatbot-messages__wrapper {
                width: 100%;
                max-width: 768px;
                margin-inline: auto;
            }

            // Outline rail: wide chats only, measured on the chat and not on
            // the viewport, so a preview frame squeezed by side panels loses
            // it like a phone does. The rail sits at the edge of the scroll
            // area beside the reading column: below the `--wide` threshold
            // (768px column plus a 40px rail on each side) it would cover the
            // messages instead.
            .pk-chatbot-messages__outline {
                display: none;
            }

            &.pk-chatbot--wide .pk-chatbot-messages__outline {
                display: block;
            }

            .pk-chatbot-file-preview {
                background-color: var(--color-surface);
            }

            .pk-chatbot-input__notice {
                background-color: var(--color-surface);
                border-inline: 1px solid var(--color-surface-3);
                border-top: 1px solid var(--color-surface-2);
                padding: var(--spacing-10) var(--spacing-16);

                // As deep as the pill's corners, or the page shows through
                // where their curve leaves the side
                &::after {
                    background-color: var(--color-surface);
                    border-inline: 1px solid var(--color-surface-2);
                    bottom: calc(-1 * var(--pk-chat-dock-radius));
                    height: var(--pk-chat-dock-radius);
                    left: -1px;
                    right: -1px;
                }
            }

            .pk-chatbot-input {
                width: calc(100% - var(--spacing-sm) * 2);
                max-width: 768px;
                margin-inline: auto;

                &__form {
                    // True semicircles at rest, concentric with the send
                    // button, whose center sits 30px in. Not `rounded-full`:
                    // once Shift+Enter grows the field the ends would swell
                    // with it and cut into the first and last line.
                    border-radius: var(--pk-chat-dock-radius);
                    min-height: var(--pk-chat-fullscreen-dock-height);
                    padding: var(--spacing-12) var(--spacing-16);
                    background-color: var(--color-surface-1);
                    border-color: var(--color-surface-2);
                    box-shadow: none;
                    transition:
                        border-color var(--duration-200) var(--ease-in-out),
                        box-shadow var(--duration-200) var(--ease-in-out);

                    &:has(:focus-within) {
                        border-color: var(--color-surface-4);
                        box-shadow: var(--shadow-sm);
                    }

                    @media (prefers-reduced-motion: reduce) {
                        transition: none;
                    }
                }

                &__textarea {
                    background-color: transparent;
                }
            }

            // A live call takes the place of the input, so it takes its shape,
            // its column and its height: full-bleed here would break the
            // reading axis, and a different height would make the dock jump
            // the moment the call starts.
            .pk-voice-session {
                --pk-chat-dock-height: var(--pk-chat-fullscreen-dock-height);
                --pk-voice-orb-size: var(--spacing-40);
                --pk-voice-orb-bars-height: var(--spacing-20);
                --pk-voice-session-surface: var(--color-surface-1);

                width: calc(100% - var(--spacing-sm) * 2);
                max-width: 768px;
                margin-inline: auto;
                padding-inline: var(--spacing-16);
                border-color: var(--color-surface-2);
                border-radius: var(--pk-chat-dock-radius);
                box-shadow: none;

                &__timer {
                    font-size: var(--text-14);
                }
            }
        }

        // Modifier: panel — compact chrome for a docked side panel: the host
        // container owns background and border, the header slims down and
        // drops its own surface so the chat blends into the panel.
        &--panel {
            background-color: transparent;

            .pk-chatbot__header {
                background-color: transparent;

                // Fixed height so the panel header lines up with the host's
                // own chrome. Custom properties inherit through the shadow
                // root: an embedding host can override it to match its
                // header (the default matches the platform header, 50px).
                height: var(--pk-chatbot-panel-header-height, 50px);
                align-items: center;
                padding-block: 0;
                padding-inline: var(--spacing-10);
                border-bottom: 1px solid var(--color-surface-3);
            }

            .pk-chatbot__title {
                font-size: var(--text-14);
            }

            .pk-chatbot__footer {
                border-top: none;
                padding: var(--spacing-6) var(--spacing-md);
            }
        }

        // A chat given a whole page without the fullscreen layout (the page
        // an iframe embeds, a widget its host lets fill the screen) keeps the
        // reading column of the fullscreen one: at 1440px an answer ran
        // 1408px wide, some two hundred characters a line. Measured on the
        // chat, like `--wide` itself, so a narrow frame is left alone.
        &--wide:not(.pk-chatbot--fullscreen) {
            .pk-chatbot-messages__wrapper,
            .pk-chatbot-input {
                width: 100%;
                max-width: 768px;
                margin-inline: auto;
            }
        }
    }
</style>
